import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, NgClass, NgIf } from '@angular/common';
import { ToastController, IonImg, IonProgressBar } from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { UserCollectionsServices } from '../../providers/user-collection-services';
import { GlobalDataService } from '../../providers/global-data.service';
import { BtnComponent } from '../btn/btn.component';
import { addIcons } from 'ionicons';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
import lottie from 'lottie-web';

@Component({
  selector: 'app-v2-open-spinners',
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    NgClass,
    TranslatePipe,
    IonImg,
    IonProgressBar,
    BtnComponent,
  ],
  templateUrl: './v2-open-spinners.component.html',
  styleUrls: ['./v2-open-spinners.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2OpenSpinnersComponent implements OnChanges, OnDestroy {
  @ViewChild('overlayRoot', { read: ElementRef }) overlayRootEl?: ElementRef<HTMLDivElement>;
  @ViewChildren('SpinnerOpen') lottieOpen!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('Spinner1') lottie1!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('Spinner2') lottie2!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('Spinner3') lottie3!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('Spinner4') lottie4!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('Spinner5') lottie5!: QueryList<ElementRef<HTMLDivElement>>;

  @Input() isOpen = false;
  @Input() agentId = '';
  @Input() collectionId = '';
  @Input() userCollectionId = '';

  @Output() closed = new EventEmitter<any>();
  /** Successful spin: same payload as `closed`, emitted ~1s before overlay dismiss. */
  @Output() presentV3Cards = new EventEmitter<any>();

  numSpinners = -1;
  numBindersToOpen = 0;
  operationInProgress = false;
  collectionIconPath = '';
  collectionTitle = '';
  pcents = 0;
  hasImgLoaded = false;

  dataLoading = true;
  headerReveal = false;
  contentReveal = false;
  private headerRevealRaf1 = 0;
  private headerRevealRaf2 = 0;

  domOpen = false;
  isClosing = false;
  private closeAnimationFinishTimer: ReturnType<typeof setTimeout> | null = null;
  private spinnerSequenceTimers: ReturnType<typeof setTimeout>[] = [];

  /** Spinner wheel UX (from legacy OpenSpinnersPage) */
  isSpinnerSpining = false;
  randomResult = 0;
  finalValue: number | undefined = undefined;

  /** Bottom tap hint on pack-style layer; hidden after user taps to spin (v3-style). */
  spinnerTapHintVisible = true;

  /**
   * Snapshot of `numSpinners` when a spin request starts, so the title badge does not
   * jump to 0 as soon as `refreshActiveCollectionFromStatus` runs (before Lottie / dismiss).
   */
  private spinSessionStartCount: number | null = null;

  private static readonly OVERLAY_CLOSE_ANIM_MS = 400;
  /** Post-spin sequence length (ms) before overlay dismiss; V3 leads by V3_DISPLAY_LEAD_MS. */
  private static readonly SPINNER_SUCCESS_DISMISS_MS = 8000;
  private static readonly V3_DISPLAY_LEAD_MS = 1000;
  private loadSeq = 0;
  private lottieInitScheduled = false;
  private activeCollectionSub?: Subscription;
  private collectionFinSub?: Subscription;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly userCollectionsService: UserCollectionsServices,
    private readonly gds: GlobalDataService,
    private readonly toastCtrl: ToastController,
    public readonly translate: TranslateService
  ) {
    addIcons({ chevronBackOutline, chevronForwardOutline });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.clearCloseAnimationFinishTimer();
        const wasClosing = this.isClosing;
        this.isClosing = false;
        this.domOpen = true;
        this.contentReveal = false;
        this.lottieInitScheduled = false;
        this.isSpinnerSpining = false;
        this.randomResult = 0;
        this.finalValue = undefined;
        this.spinSessionStartCount = null;
        this.spinnerTapHintVisible = true;
        this.setBodyScrollLock(true);
        if (wasClosing) {
          setTimeout(() => this.resetOverlayEnterAnimationStyles(), 0);
        }
        this.scheduleHeaderRevealAnimation();
        void this.loadPageData();
      } else {
        this.clearActiveCollectionSub();
        this.cancelHeaderRevealAnimation();
        this.clearSpinnerSequenceTimers();
        this.headerReveal = false;
        this.contentReveal = false;
        if (this.domOpen) {
          this.isClosing = true;
          this.scheduleCloseAnimationFinish();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.clearActiveCollectionSub();
    this.cancelHeaderRevealAnimation();
    this.clearCloseAnimationFinishTimer();
    this.clearSpinnerSequenceTimers();
    this.setBodyScrollLock(false);
  }

  private clearActiveCollectionSub(): void {
    this.activeCollectionSub?.unsubscribe();
    this.activeCollectionSub = undefined;
    this.collectionFinSub?.unsubscribe();
    this.collectionFinSub = undefined;
  }

  private async ensureCollectionFin(seq: number): Promise<void> {
    if (seq !== this.loadSeq || !this.isOpen) return;
    const snap = this.gds.getCollectionFin().getValue();
    if (snap && String(snap.collectionId) === String(this.collectionId)) {
      this.pcents = snap.binder_pcents;
      this.cdr.markForCheck();
      return;
    }
    try {
      const res: any = await this.userCollectionsService.getCollectionFin(
        this.collectionId
      );
      const row = res.resource[0];
      this.pcents = row.binder_pcents;
      this.gds.publishCollectionFin({
        collectionId: String(this.collectionId),
        binder_pcents: row.binder_pcents,
        binderNumCards: row.binderNumCards,
      });
      this.cdr.markForCheck();
    } catch (e) {
      console.log(e);
    }
  }

  private applyUserCollectionRow(row: any): void {
    this.numSpinners = row.num_spinners;
    this.numBindersToOpen = row.num_binders_to_open ?? 0;
    this.collectionIconPath = row.collection_icon_path;
    const tag = row.collection_tag ?? row.collection_name ?? '';
    this.collectionTitle = typeof tag === 'string' ? tag.trim() : '';
  }

  private matchesOverlayCollection(row: any): boolean {
    if (!row?.user_collection_id) return false;
    if (String(row.user_collection_id) !== String(this.userCollectionId)) return false;
    if (this.collectionId && row.collection_id != null) {
      return String(row.collection_id) === String(this.collectionId);
    }
    return true;
  }

  private finishLoadPageData(seq: number): void {
    if (seq !== this.loadSeq || !this.isOpen) return;
    this.dataLoading = false;
    this.cdr.markForCheck();
    setTimeout(() => {
      if (!this.isOpen) return;
      this.contentReveal = true;
      this.cdr.markForCheck();
    }, 50);
    setTimeout(() => {
      if (!this.isOpen || this.dataLoading) return;
      if (this.numSpinners > 0) {
        this.scheduleLottieInit();
      }
    }, 120);
  }

  private async loadPageData(): Promise<void> {
    const seq = ++this.loadSeq;
    if (!this.agentId || !this.collectionId || !this.userCollectionId) {
      this.dataLoading = false;
      this.cdr.markForCheck();
      return;
    }
    this.clearActiveCollectionSub();
    this.dataLoading = true;
    this.numSpinners = -1;
    this.numBindersToOpen = 0;
    this.collectionIconPath = '';
    this.collectionTitle = '';
    this.pcents = 0;
    this.hasImgLoaded = false;
    this.cdr.markForCheck();

    this.collectionFinSub = this.gds.getCollectionFin().subscribe((fin) => {
      if (seq !== this.loadSeq || !this.isOpen) return;
      if (fin && String(fin.collectionId) === String(this.collectionId)) {
        this.pcents = fin.binder_pcents;
        this.cdr.markForCheck();
      }
    });

    void this.ensureCollectionFin(seq);

    if (seq !== this.loadSeq || !this.isOpen) return;

    this.activeCollectionSub = this.gds.getActiveCollection().subscribe((data) => {
      if (seq !== this.loadSeq || !this.isOpen) return;
      if (!data?.collection) return;
      const row = data.collection;
      if (!this.matchesOverlayCollection(row)) return;
      this.applyUserCollectionRow(row);
      if (this.dataLoading) {
        this.finishLoadPageData(seq);
      } else {
        this.cdr.markForCheck();
      }
    });

    setTimeout(() => {
      if (seq !== this.loadSeq || !this.isOpen) return;
      if (!this.dataLoading) return;
      void this.loadUserCollectionFallback(seq);
    }, 400);
  }

  private async loadUserCollectionFallback(seq: number): Promise<void> {
    if (seq !== this.loadSeq || !this.isOpen || !this.dataLoading) return;
    try {
      const res: any = await this.userCollectionsService.getUserCollection(
        this.userCollectionId,
        this.agentId
      );
      const row = res.resource[0];
      this.applyUserCollectionRow(row);
      this.finishLoadPageData(seq);
    } catch (err) {
      console.log(err);
      if (seq === this.loadSeq && this.isOpen) {
        this.emitDismiss({});
      }
    }
  }

  private scheduleLottieInit(): void {
    if (this.lottieInitScheduled) return;
    this.lottieInitScheduled = true;
    requestAnimationFrame(() => {
      this.spinnerOpenIconLoader();
    });
  }

  hasLoaded(): void {
    this.hasImgLoaded = true;
    this.cdr.markForCheck();
  }

  /** Shown in title pill: server value, or pre-spin count while a spin is in progress. */
  get spinnerPillCount(): number {
    if (this.operationInProgress && this.spinSessionStartCount != null) {
      return this.spinSessionStartCount;
    }
    return this.numSpinners;
  }

  /** Badge after collection row is known (numSpinners >= 0), or during an active spin. */
  get spinnerCountBadgeVisible(): boolean {
    if (this.dataLoading) {
      return false;
    }
    if (this.numSpinners >= 0) {
      return true;
    }
    return this.operationInProgress && this.spinSessionStartCount != null;
  }

  get titlePillAriaLabel(): string {
    const title = this.translate.instant('OPEN_SPINNER_TITLE');
    if (!this.spinnerCountBadgeVisible) {
      return title;
    }
    const countHint = this.translate.instant('OPEN_SPINNER_COUNT_A11Y', {
      count: this.spinnerPillCount,
    });
    return `${title}. ${countHint}`;
  }

  onHeaderBack(): void {
    this.clearSpinnerSequenceTimers();
    this.operationInProgress = false;
    this.spinSessionStartCount = null;
    this.emitDismiss({});
  }

  private clearSpinnerSequenceTimers(): void {
    for (const t of this.spinnerSequenceTimers) {
      clearTimeout(t);
    }
    this.spinnerSequenceTimers = [];
  }

  private emitDismiss(data: any): void {
    this.closed.emit(data);
  }

  gotoOpenBinders(): void {
    this.clearSpinnerSequenceTimers();
    this.operationInProgress = false;
    this.spinSessionStartCount = null;
    const row = this.gds.getActiveCollection().getValue()?.collection;
    this.presentV3Cards.emit({
      source: 'OPEN_BINDERS',
      deferredOpenBinders: true,
      agentId: this.agentId,
      collectionId: this.collectionId,
      userCollectionId: this.userCollectionId,
      numBindersToOpen: this.numBindersToOpen ?? row?.num_binders_to_open ?? 0,
      binderCover: row?.binder_cover ?? row?.no_sticker_img ?? '',
      iconLocationPrefix: row?.icon_location_prefix ?? '',
    });
    this.emitDismiss({});
  }

  gotoGet(): void {
    this.clearSpinnerSequenceTimers();
    this.operationInProgress = false;
    this.spinSessionStartCount = null;
    this.emitDismiss({ goto: 'GetPage' });
  }

  async openSpinner(): Promise<void> {
    if (this.operationInProgress || this.numSpinners < 1) {
      return;
    }
    this.clearSpinnerSequenceTimers();
    this.spinnerTapHintVisible = false;
    this.spinSessionStartCount = this.numSpinners;
    this.operationInProgress = true;
    this.cdr.markForCheck();

    try {
      const res: any = await this.userCollectionsService.openSpinner(
        this.agentId,
        this.collectionId,
        this.userCollectionId
      );
      try {
        await this.userCollectionsService.refreshActiveCollectionFromStatus(
          this.userCollectionId,
          this.agentId.toString()
        );
      } catch (refreshErr) {
        console.error(
          'Failed to refresh active collection after open spinner',
          refreshErr
        );
      }
      this.randomResult = res.resource.p_spinner_result;
      switch (this.randomResult) {
        case 1:
          this.spinner1IconLoader();
          break;
        case 2:
          this.spinner2IconLoader();
          break;
        case 3:
          this.spinner3IconLoader();
          break;
        case 4:
          this.spinner4IconLoader();
          break;
        case 5:
          this.spinner5IconLoader();
          break;
      }
      setTimeout(() => {
        this.isSpinnerSpining = true;
        this.cdr.markForCheck();
      }, 250);

      setTimeout(() => {
        if (this.finalValue !== undefined) {
          void this.presentToast(
            this.translate.instant('FINAL_WON') +
              this.finalValue +
              this.translate.instant('NEW_ITEMS')
          );
        }
      }, 4000);

      if (this.randomResult === 0) {
        await this.presentInfoToast(this.translate.instant('OPEN_SPINNER_NONE_LEFT'));
        this.operationInProgress = false;
        this.spinSessionStartCount = null;
        this.cdr.markForCheck();
        return;
      }
      this.finalValue = this.randomResult;
      res.source = 'OPEN_SPINNER';
      const dismissMs = V2OpenSpinnersComponent.SPINNER_SUCCESS_DISMISS_MS;
      const leadMs = V2OpenSpinnersComponent.V3_DISPLAY_LEAD_MS;
      const v3At = Math.max(0, dismissMs - leadMs);
      this.spinnerSequenceTimers.push(
        setTimeout(() => {
          if (!this.isOpen) return;
          this.presentV3Cards.emit(res);
        }, v3At)
      );
      this.spinnerSequenceTimers.push(
        setTimeout(() => {
          if (!this.isOpen) return;
          this.operationInProgress = false;
          this.spinSessionStartCount = null;
          this.emitDismiss(res);
        }, dismissMs)
      );
    } catch (error) {
      console.log(error);
      this.operationInProgress = false;
      this.spinSessionStartCount = null;
      this.spinnerTapHintVisible = true;
      const toast = await this.toastCtrl.create({
        message: this.translate.instant('ERROR_OPENNING_SPINNERS'),
        duration: 3000,
        position: 'bottom',
      });
      await toast.present();
    }
    this.cdr.markForCheck();
  }

  private async presentToast(msg: string): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 2000,
      position: 'bottom',
      color: 'success',
    });
    await toast.present();
  }

  private async presentInfoToast(msg: string): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 2500,
      position: 'bottom',
    });
    await toast.present();
  }

  spinnerOpenIconLoader(): void {
    this.lottieOpen?.forEach((container) => {
      const el = container.nativeElement as HTMLElement & { _lottieLoaded?: boolean };
      if (el._lottieLoaded) return;
      const animationInstance = lottie.loadAnimation({
        container: el,
        path: './assets/icon/Spinner-open.json',
        renderer: 'svg',
        loop: false,
        autoplay: true,
      });
      el._lottieLoaded = true;
      let direction: 'forward' | 'backward' = 'forward';
      animationInstance.addEventListener('enterFrame', () => {
        const currentFrame = animationInstance.currentFrame;
        if (direction === 'forward' && currentFrame >= 358) {
          direction = 'backward';
          animationInstance.setDirection(-1);
          animationInstance.play();
        } else if (direction === 'backward' && currentFrame <= 200) {
          direction = 'forward';
          animationInstance.setDirection(1);
          animationInstance.play();
        }
      });
    });
  }

  private loadSpinnerJson(
    list: QueryList<ElementRef<HTMLDivElement>>,
    path: string
  ): void {
    list?.forEach((container) => {
      const el = container.nativeElement as HTMLElement & { _lottieLoaded?: boolean };
      if (el._lottieLoaded) return;
      lottie.loadAnimation({
        container: el,
        path,
        renderer: 'svg',
        loop: false,
        autoplay: true,
      });
      el._lottieLoaded = true;
    });
  }

  spinner1IconLoader(): void {
    this.loadSpinnerJson(this.lottie1, './assets/icon/Spinner-1.json');
  }
  spinner2IconLoader(): void {
    this.loadSpinnerJson(this.lottie2, './assets/icon/Spinner-2.json');
  }
  spinner3IconLoader(): void {
    this.loadSpinnerJson(this.lottie3, './assets/icon/Spinner-3.json');
  }
  spinner4IconLoader(): void {
    this.loadSpinnerJson(this.lottie4, './assets/icon/Spinner-4.json');
  }
  spinner5IconLoader(): void {
    this.loadSpinnerJson(this.lottie5, './assets/icon/Spinner-5.json');
  }

  private scheduleHeaderRevealAnimation(): void {
    this.cancelHeaderRevealAnimation();
    this.headerReveal = false;
    this.contentReveal = false;
    this.cdr.markForCheck();
    this.headerRevealRaf1 = requestAnimationFrame(() => {
      this.headerRevealRaf1 = 0;
      this.headerRevealRaf2 = requestAnimationFrame(() => {
        this.headerRevealRaf2 = 0;
        this.headerReveal = true;
        this.cdr.markForCheck();
      });
    });
  }

  private cancelHeaderRevealAnimation(): void {
    if (this.headerRevealRaf1) {
      cancelAnimationFrame(this.headerRevealRaf1);
      this.headerRevealRaf1 = 0;
    }
    if (this.headerRevealRaf2) {
      cancelAnimationFrame(this.headerRevealRaf2);
      this.headerRevealRaf2 = 0;
    }
  }

  private getOverlayCloseAnimDurationMs(): number {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return V2OpenSpinnersComponent.OVERLAY_CLOSE_ANIM_MS;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : V2OpenSpinnersComponent.OVERLAY_CLOSE_ANIM_MS;
  }

  private clearCloseAnimationFinishTimer(): void {
    if (this.closeAnimationFinishTimer) {
      clearTimeout(this.closeAnimationFinishTimer);
      this.closeAnimationFinishTimer = null;
    }
  }

  private scheduleCloseAnimationFinish(): void {
    this.clearCloseAnimationFinishTimer();
    const ms = this.getOverlayCloseAnimDurationMs();
    this.closeAnimationFinishTimer = setTimeout(() => {
      this.closeAnimationFinishTimer = null;
      this.isClosing = false;
      this.domOpen = false;
      this.setBodyScrollLock(false);
      this.cdr.markForCheck();
    }, ms);
  }

  private resetOverlayEnterAnimationStyles(): void {
    const el = this.overlayRootEl?.nativeElement;
    if (!el) return;
    el.style.animation = 'none';
    el.style.opacity = '1';
    el.style.transform = 'scale(1)';
    void el.offsetWidth;
    el.style.removeProperty('animation');
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
  }

  private setBodyScrollLock(locked: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('v2-open-spinners-open', locked);
  }
}
